import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

function useSaveName() {
  const { patchUser } = useAuth();
  const { i18n } = useTranslation();
  return async (name) => {
    const lang = i18n.language === "es" ? "es" : "en";
    const { data } = await api.put("/copilot/identity", { assistant_name: name, lang });
    patchUser({ assistant_name: data.assistant_name });
    return data;
  };
}

function NameForm({ initial = "", isRename, onDone, onCancel }) {
  const { t } = useTranslation();
  const save = useSaveName();
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!name.trim()) { setError(t("copilot_identity.label")); inputRef.current?.focus(); return; }
    setBusy(true);
    try {
      await save(name.trim()); // preserves entered name on failure (we don't clear it)
      toast.success(t("copilot_identity.saved"));
      onDone?.();
    } catch (e2) {
      // recoverable error: keep the typed name, show honest error, no success state
      setError(formatApiErrorDetail(e2?.response?.data?.detail) || t("copilot_identity.save_error"));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="copilot-name-form">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#B28A3D]/15 ring-1 ring-[#B28A3D]/30">
          <Sparkles className="h-5 w-5 text-[#B28A3D]" />
        </div>
        <div>
          <div className="font-heading text-lg font-bold text-[#17324D]">
            {isRename ? t("copilot_identity.rename_title") : t("copilot_identity.intro_title")}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {isRename ? t("copilot_identity.rename_body") : t("copilot_identity.intro_body")}
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="copilot-name-input" className="text-xs">{t("copilot_identity.label")}</Label>
        <Input id="copilot-name-input" ref={inputRef} value={name} maxLength={60}
          onChange={(e) => setName(e.target.value)} placeholder={t("copilot_identity.placeholder")}
          data-testid="copilot-name-input" autoComplete="off" />
        {error && <div className="text-xs text-red-600" data-testid="copilot-name-error">{error}</div>}
      </div>
      <div className="flex justify-end gap-2">
        {isRename && <Button type="button" variant="outline" onClick={onCancel} disabled={busy} data-testid="copilot-name-cancel">{t("copilot_identity.cancel")}</Button>}
        <Button type="submit" disabled={busy} data-testid="copilot-name-save">
          {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("copilot_identity.saving")}</>
                : (isRename ? t("copilot_identity.rename") : t("copilot_identity.save"))}
        </Button>
      </div>
    </form>
  );
}

/* Blocking first-login gate — cannot be dismissed until a name is saved to the backend. */
export function CopilotNamingGate() {
  const { user } = useAuth();
  if (!user || typeof user !== "object" || user.role === "vendor") return null;
  if (user.assistant_name) return null;
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-[#17324D]/70 p-4" data-testid="copilot-naming-gate">
      <div role="dialog" aria-modal="true" aria-labelledby="copilot-name-input"
        className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <NameForm onDone={() => { /* gate disappears automatically once assistant_name is set */ }} />
      </div>
    </div>
  );
}

/* Intentional rename dialog (dismissable). */
export function RenameCopilotDialog({ open, onClose }) {
  const { user } = useAuth();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/60 p-4" data-testid="copilot-rename-dialog"
      onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <NameForm isRename initial={user?.assistant_name || ""} onDone={onClose} onCancel={onClose} />
      </div>
    </div>
  );
}
